import mixpanel from "mixpanel-browser";
import { fetchFromStorage } from "./Storage";

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
const MIXPANEL_SENDING =
  String(process.env.REACT_APP_MIXPANEL_ENABLED || "").toLowerCase() === "true";
const ENABLED = Boolean(MIXPANEL_TOKEN) && MIXPANEL_SENDING;

const USER_SUPER_PROPS = [
  "user_id",
  "user_email",
  "user_name",
  "user_first_name",
  "user_last_name",
  "user_mobile",
  "user_verified",
  "authenticated",
];

const SENSITIVE_KEY_PATTERN =
  /pass(word)?|pwd|secret|token|authorization|bearer|cvc|cvv|security.?code|card.?number|card\[|access.?token|refresh.?token|api.?key|stripe|sk_live|sk_test/i;

const sanitizeProps = (props = {}) => {
  const out = {};
  Object.entries(props || {}).forEach(([key, value]) => {
    if (SENSITIVE_KEY_PATTERN.test(key)) return;
    if (value === undefined) return;
    if (value !== null && typeof value === "object") {
      try {
        out[key] = JSON.parse(JSON.stringify(value));
      } catch (e) {
        out[key] = String(value);
      }
      return;
    }
    out[key] = value;
  });
  return out;
};

const getAuthUser = (userOverride) => {
  if (userOverride) return userOverride;
  try {
    const auth = fetchFromStorage("authdata");
    return auth?.data || null;
  } catch (e) {
    return null;
  }
};

export const buildUserProps = (userOverride) => {
  const user = getAuthUser(userOverride);
  if (!user) {
    return {
      authenticated: false,
      user_id: null,
      user_email: null,
      user_name: null,
    };
  }

  return {
    authenticated: true,
    user_id: user?._id || user?.userId || null,
    user_email: user?.email || null,
    user_name: user?.name || null,
    user_first_name: user?.firstName || null,
    user_last_name: user?.lastName || null,
    user_mobile: user?.mobile || null,
    user_verified: user?.verifyAcc ?? null,
    user_country_code: user?.country_code || null,
  };
};

const registerBaseProps = () => {
  mixpanel.register({
    platform: "web",
    app: "gotheextramile-react-webapp",
    environment:
      process.env.REACT_APP_MIXPANEL_ENV ||
      process.env.REACT_APP_SENTRY_ENV ||
      process.env.NODE_ENV,
  });
};

const registerUserSuperProps = (user) => {
  const props = buildUserProps(user);
  mixpanel.register(props);
  return props;
};

export const toMixpanelEventName = (action) => {
  if (!action) return "Unknown Event";
  return String(action)
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const initMixpanel = () => {
  if (!MIXPANEL_TOKEN || !MIXPANEL_SENDING) return;

  const apiHost =
    process.env.REACT_APP_MIXPANEL_API_HOST || "https://api.mixpanel.com";

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: process.env.NODE_ENV === "development",
    track_pageview: false,
    persistence: "localStorage",
    ignore_dnt: true,
    api_host: apiHost,
    batch_requests: false,
  });

  registerBaseProps();

  const existingUser = getAuthUser();
  if (existingUser?._id || existingUser?.userId) {
    identifyMixpanelUser(existingUser);
  } else {
    registerUserSuperProps(null);
  }

  mixpanel.track("App Loaded", {
    ...buildUserProps(existingUser),
  });
};

export const identifyMixpanelUser = (user) => {
  if (!ENABLED) return;

  if (!user) {
    mixpanel.reset();
    registerBaseProps();
    registerUserSuperProps(null);
    return;
  }

  const distinctId = user?._id || user?.userId;
  if (!distinctId) {
    registerUserSuperProps(null);
    return;
  }

  mixpanel.identify(String(distinctId));
  const userProps = registerUserSuperProps(user);

  mixpanel.people.set({
    $email: user?.email || undefined,
    $name: user?.name || undefined,
    $phone: user?.mobile || undefined,
    first_name: user?.firstName || undefined,
    last_name: user?.lastName || undefined,
    verify_acc: user?.verifyAcc,
    country_code: user?.country_code || undefined,
    platform: "web",
    last_seen_at: new Date().toISOString(),
  });

  mixpanel.people.set_once({
    first_seen_platform: "web",
    signup_source: "web",
  });

  return userProps;
};

export const resetMixpanelUser = () => {
  identifyMixpanelUser(null);
};

export const trackMixpanelEvent = (action, properties = {}) => {
  if (!ENABLED) return;

  const eventName = toMixpanelEventName(action);
  const userProps = buildUserProps();

  if (userProps.user_id) {
    mixpanel.register({
      user_id: userProps.user_id,
      user_email: userProps.user_email,
      user_name: userProps.user_name,
      user_first_name: userProps.user_first_name,
      user_last_name: userProps.user_last_name,
      user_mobile: userProps.user_mobile,
      user_verified: userProps.user_verified,
      authenticated: true,
    });
  }

  mixpanel.track(eventName, {
    action_key: action,
    platform: "web",
    ...userProps,
    ...sanitizeProps(properties),
  });
};

export const trackMixpanelPageView = (location) => {
  if (!ENABLED) return;
  const pathname =
    typeof location === "string" ? location : location?.pathname || "/";
  const userProps = buildUserProps();

  mixpanel.track("Page View", {
    platform: "web",
    path: pathname,
    search: typeof location === "object" ? location?.search || "" : "",
    hash: typeof location === "object" ? location?.hash || "" : "",
    page_title:
      typeof document !== "undefined" ? document.title || undefined : undefined,
    ...userProps,
  });
};

export const sendMixpanelSmokeTest = () => {
  if (!ENABLED) {
    return { ok: false, reason: "disabled" };
  }

  const runId = `mixpanel_smoke_${Date.now()}`;
  identifyMixpanelUser({
    _id: "mixpanel-smoke-user",
    email: "mixpanel-smoke@example.com",
    name: "Mixpanel Smoke Test",
    firstName: "Mixpanel",
    lastName: "Smoke",
    verifyAcc: true,
  });

  [
    "auth.login.success",
    "cart.add_item.success",
    "cart.remove_item.success",
    "checkout.started",
    "payment.stripe.success",
    "test.started",
    "test.completed",
    "auth.logout",
  ].forEach((action) => {
    trackMixpanelEvent(action, {
      runId,
      smoke_test: true,
      amount: action.includes("payment") ? "9.99" : undefined,
      order_id: action.includes("payment") ? "order_smoke_001" : undefined,
      quiz_id: action.includes("test") ? "quiz_smoke" : undefined,
      product_id: action.includes("test") ? "product_smoke" : undefined,
    });
  });

  mixpanel.track("Mixpanel Smoke Test Complete", {
    runId,
    smoke_test: true,
    ...buildUserProps({
      _id: "mixpanel-smoke-user",
      email: "mixpanel-smoke@example.com",
      name: "Mixpanel Smoke Test",
    }),
  });

  return { ok: true, runId };
};

export { mixpanel, ENABLED as isMixpanelEnabled, USER_SUPER_PROPS };
